import { useMemo } from 'react'
import { useAuth, UserRole } from '@/contexts/AuthContext'

/**
 * Permission definitions for different features
 */
export interface Permissions {
  // Restaurant management
  canSelectAnyRestaurant: boolean
  canViewAllRestaurants: boolean

  // Menu management
  canEditMenu: boolean

  // Order management
  canViewOrders: boolean
  canManageOrderStatus: boolean

  // Opening times & holidays
  canEditOpeningTimes: boolean
  canManageHolidayTemplates: boolean // super_admin only

  // Delivery areas
  canEditDeliveryAreas: boolean

  // Statistics
  canViewStatistics: boolean // super_admin only
  canViewPlatformStatistics: boolean // super_admin only

  // AI features
  canAccessAIMenu: boolean // super_admin only — AI editors use paid AWS Bedrock

  // Admin features
  canAccessLegacyAdmin: boolean
  canManageRestaurants: boolean // super_admin only
}

/**
 * Get permissions based on role.
 * super_admin has full access, restaurant_admin has limited access to their restaurant.
 */
function getPermissions(role: UserRole | undefined): Permissions {
  if (role === 'super_admin') {
    return {
      canSelectAnyRestaurant: true,
      canViewAllRestaurants: true,
      canEditMenu: true,
      canViewOrders: true,
      canManageOrderStatus: true,
      canEditOpeningTimes: true,
      canManageHolidayTemplates: true,
      canEditDeliveryAreas: true,
      canViewStatistics: true,
      canViewPlatformStatistics: true,
      canAccessAIMenu: true,
      canAccessLegacyAdmin: true,
      canManageRestaurants: true,
    }
  }

  if (role === 'restaurant_admin') {
    return {
      canSelectAnyRestaurant: false,
      canViewAllRestaurants: false,
      canEditMenu: true,
      canViewOrders: true,
      canManageOrderStatus: true,
      canEditOpeningTimes: true,
      canManageHolidayTemplates: false,
      canEditDeliveryAreas: false,
      canViewStatistics: false,
      canViewPlatformStatistics: false,
      canAccessAIMenu: false,
      canAccessLegacyAdmin: false,
      canManageRestaurants: false,
    }
  }

  // Default: no permissions (not logged in)
  return {
    canSelectAnyRestaurant: false,
    canViewAllRestaurants: false,
    canEditMenu: false,
    canViewOrders: false,
    canManageOrderStatus: false,
    canEditOpeningTimes: false,
    canManageHolidayTemplates: false,
    canEditDeliveryAreas: false,
    canViewStatistics: false,
    canViewPlatformStatistics: false,
    canAccessAIMenu: false,
    canAccessLegacyAdmin: false,
    canManageRestaurants: false,
  }
}

/**
 * Hook to get current user's permissions based on their role.
 *
 * Usage:
 * const { canEditMenu, canManageRestaurants } = usePermissions()
 *
 * if (canManageRestaurants) {
 *   // Show restaurant management UI
 * }
 */
export function usePermissions(): Permissions & {
  isSuperAdmin: boolean
  isRestaurantAdmin: boolean
  role: UserRole | null
  restaurantId: number | null
} {
  const { user } = useAuth()

  return useMemo(() => {
    const permissions = getPermissions(user?.role)

    return {
      ...permissions,
      isSuperAdmin: user?.role === 'super_admin',
      isRestaurantAdmin: user?.role === 'restaurant_admin',
      role: user?.role ?? null,
      restaurantId: user?.restaurantId ?? null,
    }
  }, [user])
}
